import { EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ListModel } from '@app/common/models/list.model';
import { DocumentModel } from '@app/common/models/document.model';
import { AttachmentTypeModel } from '@app/common/models/attachment-type.model';
import { DisabledAttachmentType } from '@app/common/components/attachment-choose-type-form/attachment-choose-type-form.component';
import { SortModel } from '@app/common/models/sort.model';
import { AuthService } from '@app/common/services/auth.service';
import { DialogService } from '@app/common/services/dialog.service';
import { Restangular } from 'ngx-restangular';
import { UserNamePipe } from '@app/common/pipes/username.pipe';
import { AttachmentTypePipe } from '@app/common/pipes/attachment-type.pipe';
import { ErrorHandlerService } from '@app/common/services/error-handler.service';
import { UploadService } from '@app/common/services/upload.service';
import { APP_CONFIG } from '@app/common/services/config.service';
import { ClassName } from '@app/common/enums/class-name.enum';
import { ComponentModel } from '@app/common/models/component.model';
import { FileUtils } from '@app/common/utils/file.utils';
import { AlertComponent } from '@app/common/components/alert/alert.component';
import { ComponentEditComponent } from '@app/ps/components/component-edit/component-edit.component';
import { ComponentAddComponent, ComponentAddResult } from '@app/ps/components/component-add/component-add.component';
import { FolderModel } from '@app/common/models/folder.model';

export interface DocumentDestination {
  id: number;
  entityType: 'businessCase' | 'opinionRequest' | 'geometricPlanRequest' | 'title' | 'parcel' | 'subject' | 'constructionObject';
}

export abstract class DocumentsBaseComponent implements OnInit, OnDestroy {
  public static readonly VIEWER_URL = window.location.pathname + `pdf-viewer/web/viewer.html?file=`;

  @Input() documentList: ListModel<DocumentModel>;
  @Input() folder: FolderModel;
  @Input() mapEnabled = false;
  @Output() updated = new EventEmitter<boolean>();

  @Input() showDocumentRelations = false; // TODO - muze jit documents
  @Input() uploadDestination: DocumentDestination; // TODO - muze jit documents

  restBaseUrl: string;
  authToken: string;
  componentSortOrder: SortModel = { sortBy: 'timestamp', direction: 'asc' };
  editInterval = null;
  ENV = this.config.ENV;
  canEdit = false;
  user = this.authService.getUser();
  project = this.authService.getActualProject();
  systemComponents = true;
  canceledComponents = true;

  constructor(
    protected authService: AuthService,
    protected dialogService: DialogService,
    protected restangular: Restangular,
    protected usernamePipe: UserNamePipe,
    protected attachmentTypePipe: AttachmentTypePipe,
    protected errorHandlerService: ErrorHandlerService,
    protected uploadService: UploadService,
    @Inject(APP_CONFIG) protected config,
  ) {
    this.getComponentSortFunction = this.getComponentSortFunction.bind(this);
    this.onUpdateComponent = this.onUpdateComponent.bind(this);
  }

  ngOnInit() {
    this.restBaseUrl = this.authService.getActiveApplicationRestUrl();
    if (this.restBaseUrl.indexOf('http://') !== 0 && this.restBaseUrl.indexOf('https://') !== 0) {
      if (this.restBaseUrl.indexOf('/') !== 0 ) {
        this.restBaseUrl = window.location.pathname + this.restBaseUrl;
      }
      this.restBaseUrl = window.location.protocol + '//' + window.location.host + this.restBaseUrl;
    }
    this.authToken = this.authService.getToken();

    // geodet muze editovat dokumenty pouze v detailu GP
    if (this.folder && this.folder.geometricPlanRequest && this.authService.hasPermission('manage_geometric_plans')) {
      this.canEdit = true;
    } else {
      this.canEdit = this.authService.hasPermission('manipulate_files');
    }
    this.createEditInterval();
  }

  ngOnDestroy() {
    clearInterval(this.editInterval);
  }

  getComponentUrl(component: ComponentModel) {
    return `${DocumentsBaseComponent.VIEWER_URL}${this.restBaseUrl}/documents/${component.document.id}/components/${component.id}/content-pdf%3Ft%3D${this.authToken}`;
  }

  onVersionComponent(component: ComponentModel) {
    const dialog = this.dialogService.open(ComponentAddComponent, {
      data: { document: component.document, component: component },
      className: ClassName.HIGHER_DIALOG,
    });

    const sub = dialog.afterClosed.subscribe((result: ComponentAddResult) => {
      if (result) {
        const sendData: any = {
          note: result.note,
          file: result.files[0].fileEntry,
          filename: result.files[0].fileEntry.name,
        };

        return this.uploadService.upload({
          url: this.restBaseUrl + `/documents/${component.document.id}/components/${component.id}/version`,
          formData: FileUtils.objectToJsonMultipartFormData(sendData),
          headers: { Authorization: this.authService.getToken() }
        }).then(() => {
          this.updated.emit();
        });
      }
      sub.unsubscribe();
    });
  }

  onUpdateComponent(component: ComponentModel, document: DocumentModel) {
    const acquisitionDeeds = AttachmentTypeModel.getByCategory('acquisitionDeed').map(a => a.id);

    const canManipulate = (component.sourceType === 'EPD' || component.sourceType === 'MPV_INSERTED')
      && (!document.businessCase
        || (document.businessCase && !document.businessCase.heliosImportStatus)
        || !acquisitionDeeds.includes(component.attachmentType)
      );

    const dialog = this.dialogService.open(ComponentEditComponent, {
      data: {
        component: component,
        canEdit: this.canEdit,
        canManipulate: canManipulate,
        isCase: (this.folder && this.folder.businessCase)
      },
      className: ClassName.HIGHER_DIALOG,
    });

    const sub = dialog.afterClosed.subscribe((result: ComponentModel | boolean) => {
      if (result) {
        this.restangular.one('documents', document.id).one('components', component.id).customPOST(result, 'update')
          .toPromise()
          .then(() => {
            this.updated.emit();
          });
      }
      sub.unsubscribe();
    });
  }

  onEditComponent(component: ComponentModel) {
    return () => {
      return this.restangular.one('documents', component.document.id).one('components', component.id).customPOST(component, 'edit')
        .toPromise()
        .then(response => {
          if (response.success) {
            // window.open('ms-word:ofe|u|' + response.link, '_blank');
            window.open(response.link, '_blank');
            this.updated.emit();
          } else {
            this.dialogService.open(AlertComponent, {
              className: ClassName.ADJUSTED_DIALOG,
              data: {
                msg: response.errorMessage,
              },
            });
          }
        });
    };
  }

  onCancelComponent(component: ComponentModel) {
    this.dialogService.confirmDialogPromise('Opravdu chcete zrušit soubor?')
      .then((data) => {
        if (data === true) {
          this.restangular.one('documents', component.document.id).one('components', component.id).customPOST({}, 'cancel')
            .toPromise()
            .then(() => {
              this.updated.emit();
            }, this.errorHandlerService.get);
        }
      });
  }

  shouldHideComponent(component: ComponentModel) {
    return (!this.systemComponents && component.attachmentType === 'OTHER_MAILROOM')
      || (!this.canceledComponents && component.cancelled);
  }

  canCancelComponent(component: ComponentModel): boolean {
    return this.canEdit
      && !component.editLock
      && !component.cancelled
      && ['MPV_GENERATED', 'MPV_INSERTED', 'MIGRATED'].includes(component.sourceType);
  }

  canVersionComponent(component: ComponentModel): boolean {
    const businessCase = component.document.businessCase;

    return this.canEdit
      && !component.editLock
      && !component.cancelled
      && businessCase && !businessCase.heliosImportStatus && !businessCase.isSignedByAll
      && ['MPV_GENERATED', 'MPV_INSERTED'].includes(component.sourceType)
      && ['CONTRACT', 'COVERING_LETTER', 'CONTRACT_EXTERNAL', 'COVERING_LETTER_EXTERNAL'].includes(component.attachmentType);
  }

  canEditComponent(component: ComponentModel): boolean {
    const businessCase = component.document.businessCase;

    let businessCaseCheck = true;

    if (businessCase && ['CONTRACT', 'COVERING_LETTER', 'CONTRACT_EXTERNAL', 'COVERING_LETTER_EXTERNAL'].includes(component.attachmentType)) {
      businessCaseCheck = !businessCase.heliosImportStatus && !businessCase.isSignedByAll;
    }

    return this.canEdit
      && !component.cancelled
      && ['MPV_GENERATED', 'MPV_INSERTED'].includes(component.sourceType)
      && businessCaseCheck
      && (this.fileType(component, 'word') || this.fileType(component, 'excel'));
  }

  fileType(component: ComponentModel, type: 'pdf' | 'word' | 'excel' | 'vfk' | ''): boolean {
    const extension = FileUtils.getFileExtension({ name: component.baseName });

    const isWord = extension === 'docx';
    if (type === 'word' && isWord) {
      return true;
    }

    const isExcel = extension === 'xlsx';
    if (type === 'excel' && isExcel) {
      return true;
    }

    const isPdf = extension === 'pdf';
    if (type === 'pdf' && isPdf) {
      return true;
    }

    const isVfk = extension === 'vfk';
    if (type === 'vfk' && isVfk) {
      return true;
    }

    return type === '' && !isWord && !isExcel && !isPdf;
  }

  protected getComponentSortFunction(column) {
    return (value): any => {
      if (column === 'createdUser') {
        return this.usernamePipe.transform(value[column]);
      }

      if (column === 'attachmentType') {
        return this.attachmentTypePipe.transform({ type: value[column]}, 'title').toLowerCase();
      }

      return value[column];
    };
  }

  protected getUploadRelations() {
    const relations = {};
    const relationField = (this.uploadDestination.entityType === 'businessCase' ? 'case' : this.uploadDestination.entityType) + 'Id';
    relations[relationField] = this.uploadDestination.id;
    return relations;
  }

  private createEditInterval() {
    if (this.documentList.itemCount) {
      this.editInterval = setInterval(() => {
        this.restangular.one('documents/components/edited').get().toPromise().then((edited) => {
          this.documentList.list.forEach(d => {
            if (d.sourceType === 'MPV') {
              d.components.forEach(c => {
                const matched = edited.find(e => e.id === c.id);
                if ((matched && !c.editLock) || (!matched && c.editLock)) {
                  this.updated.emit();
                }
              });
            }
          });
        });
      }, 5000);
    }
  }
}
